import React, { useState } from "react";
import styles from './ContactForm.scss'
import cx from "../../utils/cx";
import TextField from "../MaterialUi/TextField";
import RequestManager from "../../utils/RequestManager";
import createToast from "../MaterialUi/Toast";
import LogError from "../../utils/LogError";
import { AtLeastOne } from "../../interfaces/TypeHelper";
import Button from "../MaterialUi/Button";

export interface INewsletterRapidmailProps {
    className?: string;
}

const exampleValidationMessage = {
    "validation_messages": {
        "email": "Diese E-Mail-Adresse wurde bereits angemeldet",
        "firstname": "",
        "lastname": "",
        "tags": "Bitte auswählen",
        "consent_text": "Bitte bestätigen"
    }
}
export type IRapidmailSubscriptionResponse = typeof exampleValidationMessage;

export type IValidationMessages = typeof exampleValidationMessage['validation_messages'];


export type IRapidmailSubscriptionFormValues = {
    email: string;
    firstname?: string;
    lastname?: string;
    consent_text: boolean;
} & AtLeastOne<{
    eigentuemer: boolean;
    erwerben: boolean;
    woechentlich: boolean;
    makler: boolean;
}>;

export const RapidmailRecipientLists = {
    WGL_NEWSLETTER: {
        id: 4619, // NL Empfänger Stand 17.09.2021
        url: 'https://newsletter.wohnglueck.de/159/4619/a2c0e3c5fb/subscribe/form.html'
    },
    B2B_MAKLER_NEWSLETTER: {
        id: 7834, // Empfängerliste: B2B NL Makler Empfänger Liste
        url: 'https://t7f4f5cae.emailsys1a.net/159/7834/c21ff72169/subscribe/form.html'
    }
}

export type IRapidmailNewsletterId = keyof typeof RapidmailRecipientLists;


export const sendRapidmailSubscriptionForm = (formValues: IRapidmailSubscriptionFormValues, newsletterId: IRapidmailNewsletterId) => {
    const formUrl = RapidmailRecipientLists[newsletterId].url;

    if (!formUrl) {
        LogError.add(new Error('No formUrl for Rapidmail subscription'), {
            title: 'No formUrl for Rapidmail subscription',
            error_code: 'NewsletterRapidmail.tsx:sendRapidmailSubscriptionForm',
            request_data: { formValues, newsletterId }
        });
        return;
    }

    const { email, firstname, lastname, consent_text } = formValues;
    const tags = Object.keys(formValues).filter(key => formValues[key] === true && key !== 'consent_text');

    const formData = new FormData();
    formData.append('email', email);
    firstname && formData.append('firstname', firstname);
    lastname && formData.append('lastname', lastname);
    formData.append('consent_text', consent_text ? 'yes' : undefined);
    tags.forEach(tag => tag === 'makler' ? null : formData.append('tags[]', tag));

    return RequestManager.postRequest(formUrl, { accept: 'application/json' }, formData)
};


const NewsletterRapidmail: React.FC<React.PropsWithChildren<INewsletterRapidmailProps>> = ({ className = '' }) => {

    const [formValues, setFormValues] = useState<Partial<IRapidmailSubscriptionFormValues>>({})
    const [validationMessages, setValidationMessages] = useState({} as IValidationMessages);

    const onChange = (ev) => {
        const name = ev.target.name;
        const value = ev.target.value || ev.target.checked;
        setFormValues(prev => ({ ...prev, [name]: value }))
    }

    const handleSubmit = (ev: React.FormEvent) => {
        ev.preventDefault();

        formValues.consent_text = true;
        formValues['woechentlich'] = true;

        if (!formValues['email']) {
            setValidationMessages({ ...validationMessages, email: 'Bitte E-Mail-Adresse angeben' });
            return;
        }

        sendRapidmailSubscriptionForm(formValues as IRapidmailSubscriptionFormValues, 'WGL_NEWSLETTER').then(() => {
            setValidationMessages({} as IValidationMessages);
            setFormValues({});
            createToast('Die Anmeldung wurde gespeichert.', "success");
        }).catch(e => {
            console.error(e);
            const { validation_messages } = e.responseData as typeof exampleValidationMessage || {};
            if (validation_messages) {
                setValidationMessages(validation_messages);
                createToast(Object.keys(validation_messages).map(key => validation_messages[key]).join(', '), "error");
            }
            LogError.add(e, {
                title: 'Fehler bei der Newsletter-Anmeldung!',
                error_code: 'NewsletterRapidmail.tsx:handleSubmit',
                request_data: { formValues, responseData: e.responseData }
            });
        });
    };


    return <div className={cx(styles.contactForm, className)}>
        <h2 className={cx(styles.title, 'infoLine')}>Immer informiert</h2>
        <hr />
        <h2>Für den wöchentlichen Newsletter von Wohnglück.de anmelden</h2>

        <form onSubmit={handleSubmit}>

            <TextField onChange={onChange} required value={formValues['email'] || ''} name="email" label="E-Mail" type="text"
                error={!!validationMessages.email} helperText={formError(validationMessages.email)} />

            <p>Ja, ich abonniere den wöchentlichen Newsletter von Wohnglück.de und bin mit der unten
                beschriebenen Nutzung meiner Daten** einverstanden. Diese Einwilligung kann
                jederzeit und ohne Angabe von Gründen mit Wirkung für die Zukunft widerrufen
                werden. Weitere Informationen über die Verarbeitung meiner Daten finde ich
                in der <a href="https://wohnglueck.de/info/datenschutz" target="_blank">Datenschutzerklärung</a>.</p>


            <div className={styles.actions}>
                <Button type="submit" color="primary">Anmelden</Button>
            </div>

            <p>Vielen Dank, dass Du die mit * markierten Pflichtfelder ausfüllst.</p>

            <p className={styles.asterisk}>
                **Datennutzung: Meine Daten werden für den regelmäßigen (i.d.R. wöchentlichen) Versand des
                Newsletters über folgende Inhalte verarbeitet: Top-Artikel, Angebote und Services auf {''}
                <a href="https://wohnglueck.de" target="_blank">Wohnglück</a> Informationen
                rund um Ver-/Kauf, Bau, Bewertung und Modernisierung von
                Immobilien, sowie Angebote unserer Werbepartner. Weiterhin willige ich ein, dass zum Zwecke
                der Verbesserung der Nutzerfreundlichkeit, unserer Angebote und unserer Werbemaßnahmen Daten
                über mein Leseverhalten (Zeitpunkt des Öffnens, der Klicks; geklickte Inhalte; Verweildauer
                im Newsletter; Standort) und Geräteinformationen verarbeitet werden sowie, dass zur Anzeige,
                Abrechnung und Optimierung von Werbemaßnahmen unserer Werbepartner meine IP-Nummer,
                Geräteinformationen, Zeitstempel, Newsletter/Kampagne an diese übermittelt werden.
            </p>

        </form>
    </div>
};

const formError = (error: string) => {
    return error && <span className={styles.error}>{error}</span>
}

export default NewsletterRapidmail;
